import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "react-phone-number-input/style.css";
import PhoneInput, {
  getCountries,
  isValidPhoneNumber,
} from "react-phone-number-input";
import {
  TbBrandInstagram,
  TbBrandWhatsapp,
  TbBrandTelegram,
  TbX,
  TbPlus,
  TbTrash,
} from "react-icons/tb";
import {
  postInstagramExclude,
  postWhatsappExclude,
  postTelegramExclude,
} from "../../api/exludeClients/excludeClientService";
import { getSocialProfile } from "../../api/profile/profileServices";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import { getLocationThunk } from "../../redux/slices/profileSlice/asyncthunk";
import { selectLocationCode } from "../../redux/slices/profileSlice/ProfileSlice";

const PLATFORMS = [
  {
    key: "Instagram",
    label: "Instagram",
    icon: TbBrandInstagram,
    color: "from-purple-500 to-pink-500",
    border: "border-pink-300",
    bg: "bg-pink-50",
    text: "text-pink-700",
    placeholder: "Type Instagram username (e.g. @username)",
    inputType: "text",
  },
  {
    key: "Whatsapp",
    label: "WhatsApp",
    icon: TbBrandWhatsapp,
    color: "from-green-400 to-emerald-500",
    border: "border-emerald-300",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    placeholder: "Type phone number (e.g. +1234567890)",
    inputType: "tel",
  },
  {
    key: "Telegram",
    label: "Telegram",
    icon: TbBrandTelegram,
    color: "from-sky-400 to-blue-500",
    border: "border-blue-300",
    bg: "bg-blue-50",
    text: "text-blue-700",
    placeholder: "Type Telegram username (e.g. @username)",
    inputType: "text",
  },
];

function PartialErrorModal({ data, onClose }) {
  const created = data?.created || [];
  const duplicates = data?.duplicates || [];
  const allDuplicates = created.length === 0 && duplicates.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4"
      >
        <p className="text-base font-medium text-gray-900 text-center mb-4">
          {allDuplicates
            ? "The excluded contact is already in your list and cannot be added again."
            : "Some contacts could not be added — they are already in your list."}
        </p>

        <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4 text-sm text-gray-700 space-y-2">
          <p>
            <span className="font-medium">Created: </span>
            {created.length}
          </p>
          {duplicates.length > 0 && (
            <div>
              <p className="font-medium">Duplicated.</p>
              <ul className="mt-1 space-y-0.5 pl-1">
                {duplicates.map((u, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-500 inline-block" />
                    {u}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl border border-[#6366F1] text-[#6366F1] text-sm font-medium hover:bg-indigo-50 transition-colors"
        >
          Ok
        </button>
      </motion.div>
    </div>
  );
}

function PlatformSelectModal({
  onSelect,
  onClose,
  isPlatformConnected,
  loadingSocial,
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-xs mx-4"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Select platform
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <TbX className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          {loadingSocial ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            </div>
          ) : (
            PLATFORMS.map((p) => {
              const Icon = p.icon;
              const connected = isPlatformConnected(p.key);
              return (
                <div key={p.key}>
                  <motion.button
                    whileHover={{ scale: connected ? 1.02 : 1 }}
                    whileTap={{ scale: connected ? 0.98 : 1 }}
                    onClick={() => connected && onSelect(p.key)}
                    disabled={!connected}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                      connected
                        ? `${p.border} ${p.bg} cursor-pointer`
                        : "border-gray-200 bg-gray-50 cursor-not-allowed opacity-50"
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg bg-gradient-to-br ${
                        connected ? p.color : "from-gray-300 to-gray-400"
                      }`}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span
                      className={`font-medium text-sm ${
                        connected ? p.text : "text-gray-400"
                      }`}
                    >
                      {p.label}
                    </span>
                    {!connected && (
                      <span className="ml-auto text-xs text-gray-400">
                        Not connected
                      </span>
                    )}
                  </motion.button>
                </div>
              );
            })
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function ExcludeClientsModal({ onClose, onSuccess }) {
  const dispatch = useAppDispatch();
  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showPlatformSelect, setShowPlatformSelect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partialError, setPartialError] = useState(null);
  const [socialProfile, setSocialProfile] = useState(null);
  const [loadingSocial, setLoadingSocial] = useState(true);
  const inputRef = useRef(null);
  const locationCode = useAppSelector(selectLocationCode);

  const normalizedLocationCode = String(
    locationCode?.country_code || "",
  ).toUpperCase();
  const defaultCountry = getCountries().includes(normalizedLocationCode)
    ? normalizedLocationCode
    : undefined;

  useEffect(() => {
    dispatch(getLocationThunk());

    const fetchSocialProfile = async () => {
      try {
        const res = await getSocialProfile();
        setSocialProfile(res.data);
      } catch (err) {
        console.error("Failed to fetch social profile:", err);
        setSocialProfile({});
      } finally {
        setLoadingSocial(false);
      }
    };
    fetchSocialProfile();
  }, [dispatch]);

  const platformMeta = selectedPlatform
    ? PLATFORMS.find((p) => p.key === selectedPlatform)
    : null;

  useEffect(() => {
    if (selectedPlatform && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedPlatform]);

  const handleAddItem = () => {
    const val = inputValue.trim();
    if (!val || !selectedPlatform) return;

    if (selectedPlatform === "Whatsapp" && !isValidPhoneNumber(val)) {
      setInputError("Please enter a valid WhatsApp number");
      return;
    }

    setItems((prev) => [
      ...prev,
      { id: Math.random(), platform: selectedPlatform, value: val },
    ]);
    setInputValue("");
    setInputError("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAddItem();
  };

  const handleDeleteItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const isPlatformConnected = (platformKey) => {
    if (!socialProfile) return false;
    const key = platformKey.toLowerCase();
    const val = socialProfile[key];
    return val !== null && val !== undefined && val !== false && val !== "";
  };

  const handleSelectPlatform = (platform) => {
    setSelectedPlatform(platform);
    setShowPlatformSelect(false);
    setInputValue("");
    setInputError("");
  };

  const canAddCurrentInput = () => {
    const value = inputValue.trim();
    if (!value) return false;
    if (selectedPlatform === "Whatsapp") {
      return isValidPhoneNumber(value);
    }
    return true;
  };

  const handleSubmit = async () => {
    if (items.length === 0) return;

    const instagramItems = items
      .filter((i) => i.platform === "Instagram")
      .map((i) => i.value.replace("@", ""));
    const whatsappItems = items
      .filter((i) => i.platform === "Whatsapp")
      .map((i) => i.value.replace(/\s/g, ""));
    const telegramItems = items
      .filter((i) => i.platform === "Telegram")
      .map((i) => i.value.replace("@", ""));

    setLoading(true);

    try {
      const requests = [];

      if (instagramItems.length > 0) {
        requests.push(postInstagramExclude(instagramItems));
      }
      if (whatsappItems.length > 0) {
        requests.push(postWhatsappExclude(whatsappItems));
      }
      if (telegramItems.length > 0) {
        requests.push(postTelegramExclude(telegramItems));
      }

      const results = await Promise.allSettled(requests);

      // Собираем все ответы с duplicates (207 или 200 с duplicates)
      const allCreated = [];
      const allDuplicates = [];

      for (const r of results) {
        if (r.status === "fulfilled") {
          const d = r.value.data;
          if (d?.created) allCreated.push(...d.created);
          if (d?.duplicates) allDuplicates.push(...d.duplicates);
        }
      }

      if (allDuplicates.length > 0) {
        setPartialError({ created: allCreated, duplicates: allDuplicates });
      } else {
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.duplicates || errData?.created) {
        setPartialError({
          created: errData.created || [],
          duplicates: errData.duplicates || [],
        });
      } else {
        console.error("Failed to exclude clients:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePartialOk = () => {
    setPartialError(null);
    onSuccess?.();
    onClose();
  };

  const itemsByPlatform = PLATFORMS.map((p) => ({
    ...p,
    items: items.filter((i) => i.platform === p.key),
  })).filter((p) => p.items.length > 0);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Exclude contacts
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Add clients to your excluded list
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <TbX className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
            {/* Platform selector + Input */}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setShowPlatformSelect(true)}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                  platformMeta
                    ? `${platformMeta.border} ${platformMeta.bg} ${platformMeta.text}`
                    : "border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300"
                }`}
              >
                {platformMeta ? (
                  <>
                    <div
                      className={`p-1 rounded-md bg-gradient-to-br ${platformMeta.color}`}
                    >
                      {React.createElement(platformMeta.icon, {
                        className: "w-3.5 h-3.5 text-white",
                      })}
                    </div>
                    {platformMeta.label}
                  </>
                ) : (
                  <>
                    <TbPlus className="w-4 h-4" />
                    Select platform
                  </>
                )}
                {platformMeta && (
                  <span className="ml-auto text-xs opacity-60">Change</span>
                )}
              </button>

              {selectedPlatform && (
                <div className="flex flex-col gap-1.5">
                  {isPlatformConnected(selectedPlatform) ? (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex gap-2">
                        {selectedPlatform === "Whatsapp" ? (
                          <div
                            className={`flex-1 px-3 py-2.5 text-sm border rounded-xl focus-within:outline-none focus-within:ring-2 transition-all ${
                              platformMeta
                                ? `${platformMeta.border} focus-within:ring-opacity-30 focus-within:ring-current`
                                : "border-gray-200 focus-within:ring-gray-200"
                            }`}
                          >
                            <PhoneInput
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry={defaultCountry}
                              value={inputValue || undefined}
                              onChange={(value) => {
                                setInputValue(value || "");
                                setInputError("");
                              }}
                              onKeyDown={handleKeyDown}
                              placeholder={platformMeta?.placeholder}
                              limitMaxLength
                              numberInputProps={{
                                className:
                                  "w-full bg-transparent outline-none text-gray-800 placeholder:text-gray-400",
                              }}
                              countrySelectProps={{
                                className:
                                  "mr-2 bg-transparent outline-none text-gray-700",
                              }}
                            />
                          </div>
                        ) : (
                          <input
                            ref={inputRef}
                            type={platformMeta?.inputType || "text"}
                            value={inputValue}
                            onChange={(e) => {
                              setInputValue(e.target.value);
                              setInputError("");
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={platformMeta?.placeholder}
                            className={`flex-1 px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all ${
                              platformMeta
                                ? `${platformMeta.border} focus:ring-opacity-30 focus:ring-current`
                                : "border-gray-200 focus:ring-gray-200"
                            }`}
                          />
                        )}
                        <AnimatePresence>
                          {canAddCurrentInput() && (
                            <motion.button
                              key="add-btn"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15 }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={handleAddItem}
                              className={`shrink-0 px-3 py-2.5 rounded-xl text-white text-sm font-medium transition-colors bg-gradient-to-br ${platformMeta?.color}`}
                            >
                              <TbPlus className="w-4 h-4" />
                            </motion.button>
                          )}
                        </AnimatePresence>
                      </div>
                      {selectedPlatform === "Whatsapp" && inputError && (
                        <p className="text-xs text-red-500">{inputError}</p>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
                        platformMeta?.border
                      } bg-gray-50`}
                    >
                      <span className="text-sm text-gray-500">
                        Your {platformMeta?.label} account is not connected.
                      </span>
                      <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">
                        Connect in Settings
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Items list grouped by platform */}
            {items.length > 0 ? (
              <div className="flex flex-col gap-3">
                {itemsByPlatform.map((group) => {
                  const Icon = group.icon;
                  return (
                    <div key={group.key}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <div
                          className={`p-1 rounded-md bg-gradient-to-br ${group.color}`}
                        >
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        <span className={`text-xs font-semibold ${group.text}`}>
                          {group.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({group.items.length})
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        {group.items.map((item) => (
                          <div
                            key={item.id}
                            className={`flex items-center justify-between px-3 py-2 rounded-xl border ${group.border} ${group.bg}`}
                          >
                            <span className={`text-sm ${group.text}`}>
                              {item.value}
                            </span>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="p-1 rounded-lg hover:bg-black/5 transition-colors ml-2 shrink-0"
                            >
                              <TbTrash className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <TbPlus className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">
                  Select a platform and add usernames or phones
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-5 pt-3 border-t border-gray-100 shrink-0">
            <motion.button
              whileHover={{ scale: items.length > 0 ? 1.01 : 1 }}
              whileTap={{ scale: items.length > 0 ? 0.99 : 1 }}
              onClick={handleSubmit}
              disabled={items.length === 0 || loading}
              className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Exclude contacts {items.length > 0 && `(${items.length})`}</>
              )}
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Platform picker modal */}
      <AnimatePresence>
        {showPlatformSelect && (
          <PlatformSelectModal
            onSelect={handleSelectPlatform}
            onClose={() => setShowPlatformSelect(false)}
            isPlatformConnected={isPlatformConnected}
            loadingSocial={loadingSocial}
          />
        )}
      </AnimatePresence>

      {/* Partial error modal */}
      <AnimatePresence>
        {partialError && (
          <PartialErrorModal data={partialError} onClose={handlePartialOk} />
        )}
      </AnimatePresence>
    </>
  );
}
